import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { useCart } from '@/Application/Selectors/cart/useCart';
import {
  isDniFeatureEnabled,
  isDniRegisteredFeatureEnabled
} from '@/config/featureFlags';
import { URL_ROUTES } from '@/constants';
import { ModalWrapper } from '@/modules/perseo-billing/components';
import { ErrorCode } from '@/modules/perseo-billing/containers/fallback/components/error-code/ErrorCode';
import { ModalContent } from '@/modules/perseo-billing/containers/fallback/components/modal-payment-fallback/ModalContent';
import { RetryButton } from '@/modules/perseo-billing/containers/fallback/components/retry-button/RetryButton';
import { useClearPaymentMethod } from '@/modules/perseo-billing/hooks';
import {
  setIsProcessingPayment,
  setPaymentError
} from '@/modules/perseo-billing/store/slices/billing';
import { useMediaQuery } from '@/modules/perseo-billing/ui/react/src';
import {
  Button,
  CardFooter,
  CardHeader
} from '@/modules/perseo-billing/ui/react/src/components/beta';
import {
  ECta,
  errorCodeCHKCOUPON001
} from '@/modules/perseo-billing/utils/error-handler/error-handler.constants';
import { redirectToUrl } from '@/modules/perseo-billing/utils/window';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';
import { useCustomerContext } from '@/sections/customer/CustomerContext';

export const ModalPaymentFallback = () => {
  const paymentError = useAppSelector((state) => state?.billing?.paymentError);
  const shippingAddress = useAppSelector(
    (state) => state?.cart?.data?.shippingAddress
  );
  const {
    customer: { data: customer }
  } = useCustomerContext();

  // Determine if user is registered and check for foreign document
  const isRegistered = customer !== null;
  const isDniEnabled = isRegistered
    ? isDniRegisteredFeatureEnabled()
    : isDniFeatureEnabled();

  // * This prevents a bug where payment button is disabled with dni
  // For registered users, check customer.documentType; for guests, check shippingAddress.documentType
  const documentType = isRegistered
    ? customer?.documentType
    : shippingAddress?.documentType;

  const isForeignDocument =
    isDniEnabled && documentType === IdentificationDocumentTypes.DNI;

  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery('xl');
  const { clearPayment } = useClearPaymentMethod();
  const cart = useCart();

  const innerCustomCode = paymentError?.errorCode ?? '';

  const handleModalClose = () => {
    // * This prevents a bug where payment button is disabled with dni
    if (!isForeignDocument) {
      clearPayment();
    }

    dispatch(setPaymentError(null));
    dispatch(setIsProcessingPayment(false));

    const cta = paymentError?.cta;
    const isWhatsAppLink = cta === ECta.IR_AL_WSP_PARIS;
    if (isWhatsAppLink) {
      window.open(URL_ROUTES.WHATSAPP_LINK, '_blank');
    }

    if (paymentError?.redirectUrl) {
      redirectToUrl(paymentError?.redirectUrl);
    }

    if (innerCustomCode === errorCodeCHKCOUPON001) {
      cart.refetch();
    }
  };

  const hasPaymentError = !!paymentError;

  return (
    <ModalWrapper
      size="lg"
      roundedType="FULL_VIEW"
      open={hasPaymentError}
      onClose={() => handleModalClose()}
    >
      <div className="co-flex co-flex-col co-overflow-auto">
        <CardHeader
          action={<Button onClick={handleModalClose} icon="CloseIcon" />}
          title=""
        />

        <ModalContent handleClick={handleModalClose} />

        {!isMobile && (
          <CardFooter className="co-flex co-flex-col co-mx-auto co-gap-16px co-justify-center co-py-32px">
            <RetryButton handleClick={handleModalClose} />
            <ErrorCode error={innerCustomCode} />
          </CardFooter>
        )}
      </div>
    </ModalWrapper>
  );
};
